import { AnonymousCredential, BlockBlobClient, newPipeline } from '@azure/storage-blob';

const UPLOAD_BLOCK_SIZE_BYTES = 8 * 1024 * 1024; // 8 MiB per staged block
const UPLOAD_BLOCK_CONCURRENCY = 4; // parallel block uploads within a single file
const UPLOAD_SINGLE_SHOT_MAX_BYTES = 32 * 1024 * 1024; // files <= this use a single PUT

/**
 * Upload a single file to Azure Blob Storage.
 *
 * Files larger than UPLOAD_SINGLE_SHOT_MAX_BYTES are split into staged blocks
 * (Put Block + Put Block List) and uploaded with bounded parallelism; smaller
 * files use a single Put Blob. The Azure SDK's retry pipeline handles transient
 * failures per request, so there is no whole-file wall-clock timeout that would
 * cap large uploads. `maxRetries` and `timeoutMs` are wired into that pipeline
 * as the per-request try count and per-try timeout.
 */
export const uploadFileWithRetry = async (
  uploadUrl: string,
  file: File,
  fileName: string,
  maxRetries: number = 3,
  timeoutMs: number = 120000
): Promise<void> => {
  const pipeline = newPipeline(new AnonymousCredential(), {
    retryOptions: {
      maxTries: maxRetries,
      tryTimeoutInMs: timeoutMs,
    },
  });

  const client = new BlockBlobClient(uploadUrl, pipeline);

  try {
    await client.uploadData(file, {
      blockSize: UPLOAD_BLOCK_SIZE_BYTES,
      concurrency: UPLOAD_BLOCK_CONCURRENCY,
      maxSingleShotSize: UPLOAD_SINGLE_SHOT_MAX_BYTES,
      blobHTTPHeaders: { blobContentType: file.type },
    });
  } catch (error: any) {
    console.error(`[Upload] Failed to upload ${fileName}:`, error?.message ?? error);
    throw new Error(`Failed to upload ${fileName}: ${error?.message || 'Unknown error'}`);
  }
};
